/**
 * MountedFolderContextMenu
 *
 * Context menu for mounted folder header: Refresh, Rename in Jam SQL, Reveal in Finder, Unmount.
 */

import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '../../../components/ui/context-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from '../../../components/ui/toaster';
import { useScriptBrowserStore } from '../store/scriptBrowserStore';
import { useMountedFolderIndexStore } from '../store/mountedFolderIndexStore';
import { scanMountedFolder } from '../services/mountedFolderScanService';
import type { MountedFolder } from '../types/scriptBrowserTypes';
import { readBlueprintMetadata } from '../../database-blueprint/services/databaseBlueprintApi';
import { openDatabaseBlueprintTab } from '../../database-blueprint/services/openDatabaseBlueprintTab';
import { openSchemaCompareWithFolderAsSource } from '../../workspace/schema-compare/services/schemaCompareNavigation';
import { LinkPickerDialog } from '../../workspace/database-blueprint/components/LinkPickerDialog';
import { useRefreshFromDbDialogStore } from '../../workspace/database-blueprint/store/refreshFromDbDialogStore';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { isDatabaseBlueprintTab } from '../../workspace/core/types/workspaceTypes';

type MountedFolderContextMenuProps = {
  folder: MountedFolder;
  children: ReactNode;
};

const isMac =
  typeof navigator !== 'undefined' &&
  navigator.platform?.toLowerCase().includes('mac');

export function MountedFolderContextMenu({
  folder,
  children,
}: MountedFolderContextMenuProps) {
  const removeMountedFolder = useScriptBrowserStore((s) => s.removeMountedFolder);
  const renameMountedFolder = useScriptBrowserStore((s) => s.renameMountedFolder);
  const updateBlueprintMetadata = useScriptBrowserStore((s) => s.updateBlueprintMetadata);
  const openRefreshDialog = useRefreshFromDbDialogStore(
    (s) => s.openRefreshDialog,
  );
  const clearFolderIndex = useMountedFolderIndexStore((s) => s.clearFolderIndex);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState(folder.label);
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Finding #8 — when there are 2+ links and no Database Blueprint tab is open
  // with a selected link, the context menu must NOT silently pick
  // `linkedDbs[0]`. We open this dialog instead.
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [linkPickerMode, setLinkPickerMode] =
    useState<'refresh' | 'apply'>('refresh');
  const linkPickerInitialLabelRef = useRef<string | undefined>(undefined);

  const isBlueprint = folder.kind === 'blueprint' && !!folder.blueprintMetadata;

  /**
   * Finding #8 — resolve the linked-DB label the context menu should act on.
   *
   *  - If the user has an open Database Blueprint tab for this folder with a
   *    `selectedLinkedDbLabel` that matches an existing link, use it.
   *  - Otherwise return undefined; the caller should open the LinkPicker
   *    dialog and ask.
   */
  const readSelectedLinkLabelFromTab = (): string | undefined => {
    const links = folder.blueprintMetadata?.linkedDbs ?? [];
    if (links.length === 0) return undefined;
    const tab = useWorkspaceStore
      .getState()
      .tabs.find(
        (t) => isDatabaseBlueprintTab(t) && t.content.blueprintFolderId === folder.id,
      );
    if (!tab || !isDatabaseBlueprintTab(tab)) return undefined;
    const selected = tab.content.selectedLinkedDbLabel;
    if (selected && links.some((l) => l.label === selected)) {
      return selected;
    }
    return undefined;
  };

  /**
   * Finding #8 — refresh against a specific link label. Hands off to the
   * globally-mounted `RefreshFromDbDialog` (drift warnings are now an
   * inline banner inside that dialog).
   */
  const refreshAgainstLink = async (linkLabel: string) => {
    if (!folder.blueprintMetadata) return;
    const link = folder.blueprintMetadata.linkedDbs.find((l) => l.label === linkLabel);
    if (!link) {
      toast.error({ title: 'Linked database missing', description: linkLabel });
      return;
    }
    // Read the manifest from disk so the dialog knows which tables (if any)
    // the user opted into the data blueprint. The MountedFolder's cached
    // snapshot intentionally omits that list to keep the renderer-side
    // store small. Failure here is non-fatal — fall back to DDL-only.
    const meta = await readBlueprintMetadata({ folderPath: folder.directoryPath });
    const blueprintDataTables =
      meta.success ? (meta.metadata.blueprintDataTables ?? []) : [];
    openRefreshDialog({
      folderPath: folder.directoryPath,
      folderId: folder.id,
      linkLabel: link.label,
      link: {
        connectionDisplayName: link.connectionDisplayName,
        database: link.database,
        engine: link.engine,
      },
      blueprintDataTables,
      onSuccess: (metadata) => {
        updateBlueprintMetadata(folder.id, {
          linkedDbs: metadata.linkedDbs.map((l) => ({
            label: l.label,
            connectionId: l.connectionId,
            connectionDisplayName: l.connectionDisplayName,
            database: l.database,
            engine: l.engine,
            lastSyncAt: l.lastSyncAt,
          })),
        });
        toast.success({
          title: 'Database Blueprint refreshed',
          description: folder.directoryPath,
        });
      },
    });
  };

  const applyAgainstLink = (linkLabel: string) => {
    const link = folder.blueprintMetadata?.linkedDbs.find((l) => l.label === linkLabel);
    if (!link) {
      toast.error({ title: 'Linked database missing', description: linkLabel });
      return;
    }
    // Async since F07 — the blueprint manifest is read first so the tab opens
    // already scoped to the blueprint's schemas/object types.
    void openSchemaCompareWithFolderAsSource(folder.directoryPath, {
      connectionId: link.connectionId,
      database: link.database,
      blueprintLinkLabel: link.label,
    });
    window.electronAPI?.analytics
      ?.track('feature:database_blueprint_apply_to_db', { engine: link.engine })
      .catch(() => {
        /* fire-and-forget */
      });
  };

  const handleRefreshFromDb = async () => {
    const links = folder.blueprintMetadata?.linkedDbs ?? [];
    if (links.length === 0) {
      toast.error({ title: 'No linked database', description: 'Blueprint folder has no linked DB.' });
      return;
    }
    if (links.length === 1) {
      await refreshAgainstLink(links[0].label);
      return;
    }
    // 2+ links — prefer the active tab's selection, else open the picker.
    const tabSelected = readSelectedLinkLabelFromTab();
    if (tabSelected) {
      await refreshAgainstLink(tabSelected);
      return;
    }
    linkPickerInitialLabelRef.current = undefined;
    setLinkPickerMode('refresh');
    setLinkPickerOpen(true);
  };

  const handleApplyToDb = () => {
    const links = folder.blueprintMetadata?.linkedDbs ?? [];
    if (links.length === 0) {
      toast.error({ title: 'No linked database', description: 'Add a linked DB before applying.' });
      return;
    }
    if (links.length === 1) {
      applyAgainstLink(links[0].label);
      return;
    }
    const tabSelected = readSelectedLinkLabelFromTab();
    if (tabSelected) {
      applyAgainstLink(tabSelected);
      return;
    }
    linkPickerInitialLabelRef.current = undefined;
    setLinkPickerMode('apply');
    setLinkPickerOpen(true);
  };

  const handleUnlinkBlueprint = () => {
    clearFolderIndex(folder.id);
    removeMountedFolder(folder.id);
  };

  useEffect(() => {
    if (!renameDialogOpen) return;
    setRenameValue(folder.label);
    requestAnimationFrame(() => {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    });
  }, [folder.label, renameDialogOpen]);

  const handleRefresh = () => {
    scanMountedFolder(folder);
  };

  const handleRenameSubmit = () => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      toast.error({ title: 'Folder name required' });
      return;
    }
    renameMountedFolder(folder.id, trimmed);
    setRenameDialogOpen(false);
  };

  const handleReveal = async () => {
    if (!window.electronAPI?.showItemInFolder) return;
    const result = await window.electronAPI.showItemInFolder(folder.directoryPath);
    if (!result.success) {
      toast.error({ title: 'Could not reveal folder', description: result.error });
    }
  };

  const handleUnmount = () => {
    clearFolderIndex(folder.id);
    removeMountedFolder(folder.id);
  };

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuContent>
          {isBlueprint ? (
            <>
              <ContextMenuItem
                onSelect={() => openDatabaseBlueprintTab(folder.id, folder.label)}
                data-testid="blueprint-ctx-open-tab"
              >
                Open Database Blueprint tab
              </ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuItem
                onSelect={handleRefreshFromDb}
                data-testid="blueprint-ctx-refresh"
              >
                Refresh from DB
              </ContextMenuItem>
              <ContextMenuItem onSelect={handleApplyToDb} data-testid="blueprint-ctx-apply">
                Apply schema to DB…
              </ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuItem
                onSelect={() => setRenameDialogOpen(true)}
                data-testid="blueprint-ctx-rename"
              >
                Rename folder
              </ContextMenuItem>
              <ContextMenuItem onSelect={handleReveal}>
                {isMac ? 'Reveal in Finder' : 'Show in Explorer'}
              </ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuItem
                onSelect={handleUnlinkBlueprint}
                className="text-destructive focus:text-destructive"
                data-testid="blueprint-ctx-unlink"
              >
                Unlink blueprint
              </ContextMenuItem>
            </>
          ) : (
            <>
              <ContextMenuItem onSelect={handleRefresh}>Refresh</ContextMenuItem>
              <ContextMenuItem onSelect={() => setRenameDialogOpen(true)}>
                Rename in Jam SQL
              </ContextMenuItem>
              <ContextMenuItem onSelect={handleReveal}>
                {isMac ? 'Reveal in Finder' : 'Show in Explorer'}
              </ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuItem
                onSelect={handleUnmount}
                className="text-destructive focus:text-destructive"
              >
                Unmount Folder
              </ContextMenuItem>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>

      {isBlueprint && (
        <LinkPickerDialog
          open={linkPickerOpen}
          onOpenChange={setLinkPickerOpen}
          links={folder.blueprintMetadata?.linkedDbs ?? []}
          initialLabel={linkPickerInitialLabelRef.current}
          actionLabel={linkPickerMode === 'refresh' ? 'Refresh from DB' : 'Apply schema to DB…'}
          title={
            linkPickerMode === 'refresh'
              ? 'Pick a linked database to refresh from'
              : 'Pick a linked database to apply to'
          }
          description={
            linkPickerMode === 'refresh'
              ? 'This folder blueprints more than one database. Choose which environment you want to refresh from.'
              : 'This folder blueprints more than one database. Choose which environment you want to apply the schema to.'
          }
          onConfirm={(label) => {
            setLinkPickerOpen(false);
            if (linkPickerMode === 'refresh') {
              void refreshAgainstLink(label);
            } else {
              applyAgainstLink(label);
            }
          }}
        />
      )}

      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename mounted folder</DialogTitle>
            <DialogDescription>
              Renames the folder label inside Jam SQL Studio only. It does not rename the
              folder on disk.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            <Input
              ref={renameInputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleRenameSubmit();
                  return;
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setRenameDialogOpen(false);
                }
              }}
              placeholder="Folder name"
              data-testid="mounted-folder-rename-input"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setRenameDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleRenameSubmit}>
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
